import { ref, watch, computed } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import store from '@/store'
import router from '@/router/index'

// Notification

export default function useQuotationList() {
  // Use toast
  const toast = useToast()
  const route = router.history.current

  const refQuotationRequestsTable = ref(null)

  // Table Handlers
  const tableColumns = [
    {
      key: 'caseID',
      label: 'Case number',
      sortable: true,
    },
    {
      key: 'receivedDate',
      label: 'PRF Received On',
      sortable: true,
    },
    {
      key: 'purpose',
      label: 'Justification',
    },
    {
      key: 'requestedBy',
      label: 'Requester',
    },
    {
      key: 'vendor',
      label: 'vendor',
      sortable: true,
    },
    {
      key: 'company',
      label: 'Entity',
      sortable: true,
    },
    {
      key: 'invoices',
      label: 'Invoice ID',
      sortable: false,
    },
    {
      key: 'pendingSignatory',
      label: 'pending Signatory',
      sortable: false,
    },
    {
      key: 'assignedTo',
      label: 'Assigned to',
      sortable: true,
    },
    {
      key: 'stage',
      label: 'Stage',
      sortable: true,
    },
    { key: 'status', sortable: true },
    {
      key: 'budgetStatus',
      label: 'budget Status',
      sortable: true,
    },
    { key: 'actions' },
  ]
  const perPage = ref(route.query.perPage ?? process.env.VUE_APP_PER_PAGE_CONTENT)
  const totalPurchaseRequests = ref(0)
  const currentPage = ref(route.query.page ?? 1)
  const perPageOptions = [10, 25, 50, 100]
  const searchQuery = ref(route.query.search ?? '')
  const sortBy = ref(route.query.sortBy ?? 'stringID')
  const isSortDirDesc = ref(route.query.sortDesc === 'true')
  const statusFilter = ref([])
  if (Array.isArray(route.query.status)) {
    statusFilter.value = route.query.status
  } else if (route.query.status) {
    statusFilter.value = [route.query.status]
  }
  const assigneeFilter = ref([])
  if (Array.isArray(route.query.assignee)) {
    assigneeFilter.value = route.query.assignee
  } else if (route.query.assignee) {
    assigneeFilter.value = [route.query.assignee]
  }
  const toDeletedID = ref(null)
  const type = ref('in-quotation')
  const purchaseRequestData = ref([])
  const createdAtFilter = ref(route.query.createdAt || '')
  let initialLoad = true

  const dataMeta = computed(() => {
    const localItemsCount = refQuotationRequestsTable.value ? refQuotationRequestsTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalPurchaseRequests.value,
    }
  })

  const refetchData = () => {
    refQuotationRequestsTable.value.refresh()
  }

  watch([searchQuery, statusFilter, createdAtFilter, assigneeFilter], () => {
    currentPage.value = 1
    refetchData()
  }, { deep: true })

  watch([currentPage], () => {
    refetchData()
  }, { deep: true })

  const fetchPurchaseRequests = (ctx, callback) => {
    store
      .dispatch('app-quotation-purchase-request/fetchPurchaseRequests', {
        search: searchQuery.value,
        perPage: perPage.value,
        page: currentPage.value,
        sortBy: sortBy.value,
        sortDesc: isSortDirDesc.value,
        status: statusFilter.value,
        createdAt: createdAtFilter.value,
        assignee: assigneeFilter.value,
        type: type.value,
      },
      { root: true })
      .then(response => {
        const { purchaseRequests, total } = response.data
        purchaseRequestData.value = purchaseRequests
        callback(purchaseRequests)
        totalPurchaseRequests.value = total
        if (!initialLoad) {
          router.replace({
            query: {
              search: searchQuery.value,
              perPage: perPage.value,
              page: currentPage.value,
              sortBy: sortBy.value,
              sortDesc: isSortDirDesc.value,
              status: statusFilter.value,
              assignee: assigneeFilter.value,
              createdAt: createdAtFilter.value,
            },
          }).catch(() => {})
        }
        initialLoad = false
      })
      .catch(error => {
        toast({
          component: ToastificationContent,
          props: {
            title: error.data.message || process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  const deletePurchaseRequest = () => {
    store
      .dispatch('app-quotation-purchase-request/deletePurchaseRequest', {
        id: toDeletedID.value,
      },
      { root: true })
      .then(response => {
        refetchData()
        toast({
          component: ToastificationContent,
          props: {
            title: response.data.message ?? '',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
      .catch(error => {
        toast({
          component: ToastificationContent,
          props: {
            title: error.data.message ?? process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  // *===============================================---*
  // *--------- UI ---------------------------------------*
  // *===============================================---*

  const resolvePurchaseRequestStageVariant = stage => {
    if (stage === 'in-request') return 'danger'
    if (stage === 'in-quotation') return 'danger'
    if (stage === 'in-order') return 'danger'
    if (stage === 'in-delivery') return 'danger'
    return 'primary'
  }

  const resolvePurchaseRequestStatusVariant = status => {
    if (status === 'draft') return 'secondary'
    if (status === 'pending approval') return 'warning'
    if (status === 'pending edit') return 'warning'
    if (status === 'verifying') return 'info'
    if (status === 'verified') return 'success'
    if (status === 'cancelled') return 'danger'
    if (status === 'completed') return 'success'
    return 'primary'
  }

  return {
    fetchPurchaseRequests,
    tableColumns,
    perPage,
    currentPage,
    totalPurchaseRequests,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refQuotationRequestsTable,

    resolvePurchaseRequestStageVariant,
    resolvePurchaseRequestStatusVariant,
    refetchData,

    toDeletedID,
    deletePurchaseRequest,
    purchaseRequestData,

    // Extra Filters
    statusFilter,
    createdAtFilter,
    assigneeFilter,
  }
}
